// import { TbProgressCheck } from "react-icons/tb";
import { UseQueryResult, useQuery } from "react-query";
import { OptionsFilter } from "../../@types";
import { ResponsePanelApiDefault } from "../../@types/panelTypes";
import api from "../../service/api";
import { Client } from "./useClients";
import { OrderConfirm } from "./useOrdersCofirmes";

export type GridItem = {
  size: string;
  barcode: string;
  qtd: string;
};

export type Product = {
  cod: number;
  reference: string;
  description: string;
  grid: string;
  origin: string;
  primaryColor?: string;
  secondaryColor?: string;
  ncm?: string;
  measuredUnit?: string;

  brand?: {
    cod: string;
    name: string;
  };

  grids?: GridItem[];
};

export type OrderItem = {
  id: string;
  sequence: number;
  quantity: number;
  netValue: string;
  discountValue: string;
  stockUnit: string;
  canceled?: { cod: number; name: string };
  rejection?: { cod: number; name: string };
  status: {
    color: string;
    description: string;
  };
  product: Product;
  image: string;

  order: Order;
  orderCodeAndInitials: string;
};

export type Seller = {
  cod: number;
  abbreviation: string;
  fullName: string;
  email: string;
  phone: string;
};

export type ClientAddress = {
  city: string;
  neighborhood: string;
  numberStreet: number;
  street: string;
  uf: string;
  zip_code: string;
};

export type Brand = {
  cod: number;
  name: string;
};

export type Order = {
  id: string;
  code: number;
  initialsOrder: string;
  totalMerchandise: string;
  totalInvoiceValue: string;
  entryDate: Date;
  invoiceDate: Date;
  invoiceNumber: Date;
  status: { description: string; color: string };
  seller?: Seller;
  client?: Client;
  brand?: Brand;
  items?: OrderItem[];
  paymentMethod: string;
  origin: {
    name: string;
  };
  species: {
    cod: number;
    name: string;
  };

  tracking?: Tracking;
  confirm?: OrderConfirm;
};

type Tracking = {
  cod: string;
  description: string;
  expectedDate?: Date;
  deliveryDate?: Date;
  date: Date;
  isDelivered: Boolean;
};

export type OrderFilters = {
  cod?: string;
  documentNumber?: string;

  codClient?: string;
  cnpj?: string;
  company_name?: string;

  rejection?: string;
  canceled?: string;

  status?: string;
  statusItem?: string;
  scores?: string;
  scoresCod?: string;
  codSeller?: number;
  brand?: string;

  orderPeriod?: number;
  orderPresetDate?: number;
  orderSpecificDate?: Date;
  orderStartDate?: Date;
  orderEndDate?: Date;

  billingOrderPeriod?: number;
  billingOrderPresetDate?: number;
  billingOrderSpecificDate?: Date;
  billingOrderStartDate?: Date;
  billingOrderEndDate?: Date;

  orderBy?: string;
  isReport?: boolean;
};

export type GetOrdersResponse = {
  orders: Order[];
  totalCount: number;
  registersPerPage: number;
};

export type GetOrdersItemsResponse = {
  items: OrderItem[];
  totalCount: number;
  registersPerPage: number;
};

export type GetClientsOrderResponse = {
  clients: Client[];
  totalCount: number;
  registersPerPage: number;
};

export interface OrderFilterOptions {
  sellers: OptionsFilter[];
  originCode: OptionsFilter[];
  brands: OptionsFilter[];
  status: OptionsFilter[];
  canceled: OptionsFilter[];
  rejection: OptionsFilter[];
  statusItem: OptionsFilter[];
  scores: OptionsFilter[];
}

interface OrderApiResponse extends ResponsePanelApiDefault {
  contents: Order[];
  filters: OrderFilterOptions;
}

interface OrderItemApiResponse extends ResponsePanelApiDefault {
  contents: OrderItem[];
  filters: OrderFilterOptions;
}

interface ClientOrderApiResponse extends ResponsePanelApiDefault {
  contents: Client[];
  filters: OrderFilterOptions;
}

export async function getOrders(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
): Promise<GetOrdersResponse> {
  const { data } = await api.get<OrderApiResponse>("/orderV2", {
    params: {
      pageRequest: page - 1,
      limitRequest: registersPerPag,
      ...filter,
    },
  });

  const normalized = data.contents.map((order) => order);

  return {
    totalCount: data.total,
    orders: normalized,
    registersPerPage: data.limit,
  };
}

export function useOrders(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
): UseQueryResult<GetOrdersResponse> {
  return useQuery(
    ["orders", page, filter],
    () => getOrders(page, filter, registersPerPag),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}

export async function getOneOrder(id?: number): Promise<Order | undefined> {
  if (!id && id !== 0) return undefined;

  const { data } = await api.get<Order>(`/orderV2/${id}`);

  return data;
}

export function useOneOrder(id?: number): UseQueryResult<Order> | undefined {
  return useQuery(["order", id], () => getOneOrder(id), {
    staleTime: 1000 * 60 * 10, // 10 Minutos
  });
}

export async function getOrderFilters(filters?: OrderFilters) {
  const { data } = await api.get<OrderFilterOptions>("/orderV2/get-filters", {
    params: {
      ...filters,
    },
  });

  return data;
}

export function useOrderFilters(
  filters?: OrderFilters
): UseQueryResult<OrderFilterOptions> {
  return useQuery(["order-filters", filters], () => getOrderFilters(filters), {
    staleTime: 1000 * 60 * 10, // 10 Minutos
  });
}

export async function getOrderItems(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
) {
  const { data } = await api.get<OrderItemApiResponse>("/orderV2/items", {
    params: {
      pageRequest: page - 1,
      limitRequest: registersPerPag,
      ...filter,
    },
  });

  return {
    totalCount: data.total,
    items: data.contents.map((item) => ({
      ...item,
      image: `https://alpar2.sfo3.digitaloceanspaces.com/Produtos/${item.product.reference}_01_smaller`,
    })),
    registersPerPage: data.limit,
  };
}

export function useOrderItems(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
): UseQueryResult<GetOrdersItemsResponse> {
  return useQuery(
    ["order-items", page, filter, registersPerPag],
    () => getOrderItems(page, filter, registersPerPag),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}

export async function getClientsOrder(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
) {
  const { data } = await api.get<ClientOrderApiResponse>("/orderV2/clients", {
    params: {
      pageRequest: page - 1,
      limitRequest: registersPerPag,
      ...filter,
    },
  });

  return {
    totalCount: data.total,
    clients: data.contents,
    registersPerPage: data.limit,
  };
}

export function useClientsOrder(
  page: number,
  filter?: OrderFilters,
  registersPerPag?: number
): UseQueryResult<GetClientsOrderResponse> {
  return useQuery(
    ["clients-order", page, filter, registersPerPag],
    () => getClientsOrder(page, filter, registersPerPag),
    {
      staleTime: 1000 * 60 * 10, // 10 Minutos
    }
  );
}
